/**
 * Client upload service
 *
 * Talks to the Admin API for the standalone client-video upload feature:
 *   1. Request a presigned PUT URL, then upload the file directly to S3.
 *   2. List the tenant's previous uploads.
 *   3. Resolve a presigned download URL on demand.
 *
 * The direct-to-S3 PUT/GET requests deliberately do NOT carry the Kinde
 * Authorization header — only the API calls do.
 */

/** Maximum upload size: 2GB. Mirrors the server-side cap. */
export const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024;

/** Allowed video file extensions (mirrors the server-side allow-list). */
export const ALLOWED_EXTENSIONS = ["mp4", "mov", "avi", "mkv", "webm"] as const;

/** A previously uploaded file, as returned by the list endpoint. */
export interface ClientUpload {
	filename: string;
	key: string;
	lastModified: null | string;
	sizeBytes: number;
}

/** Upload progress callback (0-100). */
export type UploadProgressCallback = (progress: number) => void;

interface PresignedUrlResponse {
	presignedUrl: string;
	s3Key: string;
}

/**
 * Resolve a presigned download URL for one of the tenant's uploads.
 */
export async function getDownloadUrl(
	key: string,
	accessToken: string,
): Promise<string> {
	const response = await fetch(
		`${getApiUrl()}/upload/client-video/download?key=${encodeURIComponent(key)}`,
		{
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
			method: "GET",
		},
	);

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message || `Failed to get download URL: ${response.status}`,
		);
	}

	const data: { downloadUrl: string } = await response.json();
	return data.downloadUrl;
}

/**
 * List the authenticated tenant's previous uploads.
 */
export async function listUploads(
	accessToken: string,
): Promise<ClientUpload[]> {
	const response = await fetch(`${getApiUrl()}/upload/client-video`, {
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
		method: "GET",
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message || `Failed to list uploads: ${response.status}`,
		);
	}

	const data: { uploads: ClientUpload[] } = await response.json();
	return data.uploads;
}

/**
 * Upload a video file: request a presigned URL, then PUT it to S3.
 *
 * @returns The S3 key of the uploaded object.
 */
export async function uploadVideo(
	file: File,
	accessToken: string,
	onProgress?: UploadProgressCallback,
): Promise<string> {
	const { presignedUrl, s3Key } = await requestPresignedUrl(file, accessToken);
	await uploadToS3(file, presignedUrl, onProgress);
	return s3Key;
}

/**
 * Derive the S3 ContentType for a filename from its extension.
 */
function contentTypeForFilename(filename: string): string {
	const extension = filename.toLowerCase().split(".").pop();
	switch (extension) {
		case "avi":
			return "video/x-msvideo";
		case "mkv":
			return "video/x-matroska";
		case "mov":
			return "video/quicktime";
		case "webm":
			return "video/webm";
		case "mp4":
		default:
			return "video/mp4";
	}
}

/**
 * Get the API URL from environment.
 */
function getApiUrl(): string {
	const apiUrl = import.meta.env.VITE_API_URL;
	if (!apiUrl) {
		throw new Error("VITE_API_URL environment variable is not configured");
	}
	return apiUrl;
}

/**
 * Request a presigned PUT URL from the API.
 */
async function requestPresignedUrl(
	file: File,
	accessToken: string,
): Promise<PresignedUrlResponse> {
	const response = await fetch(`${getApiUrl()}/upload/client-video`, {
		body: JSON.stringify({ filename: file.name }),
		headers: {
			Authorization: `Bearer ${accessToken}`,
			"Content-Length": String(file.size),
			"Content-Type": "application/json",
		},
		method: "POST",
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message || `Failed to get presigned URL: ${response.status}`,
		);
	}

	return response.json();
}

/**
 * Upload a file to S3 using a presigned PUT URL, with progress tracking.
 */
function uploadToS3(
	file: File,
	presignedUrl: string,
	onProgress?: UploadProgressCallback,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();

		xhr.upload.addEventListener("progress", (event) => {
			if (event.lengthComputable && onProgress) {
				onProgress(Math.round((event.loaded / event.total) * 100));
			}
		});

		xhr.addEventListener("load", () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve();
			} else {
				reject(new Error(`S3 upload failed with status ${xhr.status}`));
			}
		});

		xhr.addEventListener("error", () => {
			reject(new Error("Network error during upload"));
		});

		xhr.open("PUT", presignedUrl);
		xhr.setRequestHeader("Content-Type", contentTypeForFilename(file.name));
		xhr.send(file);
	});
}
